/**
 * TA6 Property Information Form — Disclosure Questions
 *
 * Based on the Law Society TA6 (6th edition, 2020) section structure.
 * Each question maps to a specific TA6 field. Sellers answer these
 * directly; document extraction can pre-fill where evidence exists.
 */

export type AnswerType = 'yesNoNotKnown' | 'text' | 'multiChoice' | 'yesNo';

export interface TA6Question {
  id: string;
  section: number;
  sectionTitle: string;
  questionText: string;
  helpText?: string;
  answerType: AnswerType;
  choices?: string[];
  /** Only show this question when the referenced question has this answer */
  conditionalOn?: { questionId: string; answer: string };
  /** Maps to a TA6 field ID in the backend field mappings */
  fieldId?: string;
}

export interface TA6Section {
  section: number;
  title: string;
  description?: string;
  questions: TA6Question[];
}

const ta6Questions: TA6Question[] = [
  // ── Section 1: Boundaries ──────────────────────────────────────────
  {
    id: 'ta6-1.1',
    section: 1,
    sectionTitle: 'Boundaries',
    questionText: 'Who owns the boundary fences, walls, and hedges?',
    helpText: 'Indicate which boundaries you believe you own or maintain. Check your title plan if unsure.',
    answerType: 'text',
    fieldId: '1.1',
  },
  {
    id: 'ta6-1.2',
    section: 1,
    sectionTitle: 'Boundaries',
    questionText: 'Has the boundary position ever been moved or disputed?',
    answerType: 'yesNoNotKnown',
    fieldId: '1.2',
  },
  {
    id: 'ta6-1.2a',
    section: 1,
    sectionTitle: 'Boundaries',
    questionText: 'Please give details of the boundary dispute or change.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-1.2', answer: 'yes' },
  },

  // ── Section 2: Disputes and complaints ─────────────────────────────
  {
    id: 'ta6-2.1',
    section: 2,
    sectionTitle: 'Disputes and complaints',
    questionText: 'Have you had any disputes or complaints with neighbours or other parties about this property?',
    answerType: 'yesNoNotKnown',
    fieldId: '2.1',
  },
  {
    id: 'ta6-2.1a',
    section: 2,
    sectionTitle: 'Disputes and complaints',
    questionText: 'Please give details of the dispute or complaint.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-2.1', answer: 'yes' },
  },
  {
    id: 'ta6-2.2',
    section: 2,
    sectionTitle: 'Disputes and complaints',
    questionText: 'Have you received any complaints about anything you have done to the property?',
    answerType: 'yesNoNotKnown',
    fieldId: '2.2',
  },
  {
    id: 'ta6-2.3',
    section: 2,
    sectionTitle: 'Disputes and complaints',
    questionText: 'Have you made any complaints about what a neighbour has done to their property?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-2.4',
    section: 2,
    sectionTitle: 'Disputes and complaints',
    questionText: 'Are you aware of anything that might lead to a dispute about the property?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 3: Notices ─────────────────────────────────────────────
  {
    id: 'ta6-3.1',
    section: 3,
    sectionTitle: 'Notices',
    questionText: 'Have you received any notices or correspondence from any public authority or neighbour affecting the property?',
    helpText: 'This includes notices from the council, planning authority, highways, or environmental agencies.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-3.1a',
    section: 3,
    sectionTitle: 'Notices',
    questionText: 'Please give details of the notices received.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-3.1', answer: 'yes' },
  },
  {
    id: 'ta6-3.2',
    section: 3,
    sectionTitle: 'Notices',
    questionText: 'Have you sent any notices to a neighbour or public authority about the property?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 4: Alterations, planning and building control ──────────
  {
    id: 'ta6-4.1',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Have any alterations or additions been made to the property?',
    helpText: 'This includes extensions, loft conversions, structural changes, new windows, or changes to the layout.',
    answerType: 'yesNoNotKnown',
    fieldId: '4.1',
  },
  {
    id: 'ta6-4.1a',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Please describe the alterations made.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-4.1', answer: 'yes' },
  },
  {
    id: 'ta6-4.2',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Was planning permission obtained for the alterations?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.1', answer: 'yes' },
    fieldId: '4.2',
  },
  {
    id: 'ta6-4.3',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Were all planning conditions complied with?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.2', answer: 'yes' },
    fieldId: '4.3',
  },
  {
    id: 'ta6-4.4',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Was building regulations approval obtained?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.1', answer: 'yes' },
    fieldId: '4.4',
  },
  {
    id: 'ta6-4.5',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Was a building control completion certificate issued?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.4', answer: 'yes' },
    fieldId: '4.5',
  },
  {
    id: 'ta6-4.6',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Was a party wall notice served for any of the works?',
    helpText: 'A party wall notice is required under the Party Wall etc. Act 1996 before carrying out work on or near a shared wall, boundary, or excavation close to a neighbour\'s property.',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.1', answer: 'yes' },
  },
  {
    id: 'ta6-4.6a',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Is there a party wall agreement or award in place?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.6', answer: 'yes' },
  },
  {
    id: 'ta6-4.7',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Is the property a listed building?',
    helpText: 'Listed buildings are protected by law. Any works affecting the character of the building require Listed Building Consent, including internal alterations.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-4.7a',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Was Listed Building Consent obtained for any alterations?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-4.7', answer: 'yes' },
  },
  {
    id: 'ta6-4.8',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Is the property in a conservation area?',
    helpText: 'Conservation areas have additional planning restrictions. Permitted development rights may be reduced, and demolition or tree works may require consent.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-4.8a',
    section: 4,
    sectionTitle: 'Alterations, planning and building control',
    questionText: 'Are you aware of any restrictions that apply because of the conservation area?',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-4.8', answer: 'yes' },
  },

  // ── Section 5: Guarantees and warranties ───────────────────────────
  {
    id: 'ta6-5.1',
    section: 5,
    sectionTitle: 'Guarantees and warranties',
    questionText: 'Are there any guarantees or warranties for work carried out on the property?',
    helpText: 'For example, damp-proofing, timber treatment, double glazing, roofing, or NHBC/Zurich warranties.',
    answerType: 'yesNoNotKnown',
    fieldId: '5.1',
  },
  {
    id: 'ta6-5.1a',
    section: 5,
    sectionTitle: 'Guarantees and warranties',
    questionText: 'Please list the guarantees or warranties and their expiry dates.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-5.1', answer: 'yes' },
    fieldId: '5.2',
  },
  {
    id: 'ta6-5.2',
    section: 5,
    sectionTitle: 'Guarantees and warranties',
    questionText: 'Have you made any claims under these guarantees or warranties?',
    answerType: 'yesNoNotKnown',
    conditionalOn: { questionId: 'ta6-5.1', answer: 'yes' },
  },

  // ── Section 6: Insurance ───────────────────────────────────────────
  {
    id: 'ta6-6.1',
    section: 6,
    sectionTitle: 'Insurance',
    questionText: 'Is the property insured for buildings cover?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-6.2',
    section: 6,
    sectionTitle: 'Insurance',
    questionText: 'Have you ever had a buildings insurance claim refused or had special terms imposed?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-6.2a',
    section: 6,
    sectionTitle: 'Insurance',
    questionText: 'Please give details of the refused claim or special terms.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-6.2', answer: 'yes' },
  },
  {
    id: 'ta6-6.3',
    section: 6,
    sectionTitle: 'Insurance',
    questionText: 'Are you aware of anything that might affect the ability to insure the property?',
    helpText: 'For example, subsidence, flooding history, or proximity to commercial premises.',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 7: Environmental matters ───────────────────────────────
  {
    id: 'ta6-7.1',
    section: 7,
    sectionTitle: 'Environmental matters',
    questionText: 'Has the property ever been flooded?',
    answerType: 'yesNoNotKnown',
    fieldId: '13.1',
  },
  {
    id: 'ta6-7.1a',
    section: 7,
    sectionTitle: 'Environmental matters',
    questionText: 'Please give details of the flooding (dates, extent, source).',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-7.1', answer: 'yes' },
  },
  {
    id: 'ta6-7.2',
    section: 7,
    sectionTitle: 'Environmental matters',
    questionText: 'Are you aware of any environmental issues affecting the property?',
    helpText: 'For example, contaminated land, radon, Japanese knotweed, or nearby landfill.',
    answerType: 'yesNoNotKnown',
    fieldId: '12.1',
  },
  {
    id: 'ta6-7.3',
    section: 7,
    sectionTitle: 'Environmental matters',
    questionText: 'Is the property in an area that has experienced subsidence, heave, or landslip?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-7.4',
    section: 7,
    sectionTitle: 'Environmental matters',
    questionText: 'Is the property affected by any mining activity?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-7.5',
    section: 7,
    sectionTitle: 'Environmental matters',
    questionText: 'Have you carried out any energy efficiency improvements (e.g. insulation, solar panels)?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 8: Rights and informal arrangements ────────────────────
  {
    id: 'ta6-8.1',
    section: 8,
    sectionTitle: 'Rights and informal arrangements',
    questionText: 'Are you aware of any rights of way, shared access, or easements affecting the property?',
    answerType: 'yesNoNotKnown',
    fieldId: '10.1',
  },
  {
    id: 'ta6-8.1a',
    section: 8,
    sectionTitle: 'Rights and informal arrangements',
    questionText: 'Please describe the rights or easements.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-8.1', answer: 'yes' },
  },
  {
    id: 'ta6-8.2',
    section: 8,
    sectionTitle: 'Rights and informal arrangements',
    questionText: 'Are there any informal arrangements with neighbours (e.g. shared driveways, access)?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-8.3',
    section: 8,
    sectionTitle: 'Rights and informal arrangements',
    questionText: 'Do any third parties have any rights over the property?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-8.4',
    section: 8,
    sectionTitle: 'Rights and informal arrangements',
    questionText: 'Are there any overriding interests affecting the property?',
    helpText: 'Overriding interests are rights that bind a buyer even if not registered, such as rights of persons in actual occupation.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-8.5',
    section: 8,
    sectionTitle: 'Rights and informal arrangements',
    questionText: 'Are you aware of any rights to light or air that affect the property or neighbouring properties?',
    helpText: 'A right to light can prevent or restrict building work that would block natural light to a window that has received light for 20 or more years.',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 9: Parking ─────────────────────────────────────────────
  {
    id: 'ta6-9.1',
    section: 9,
    sectionTitle: 'Parking',
    questionText: 'Are there any allocated parking spaces for the property?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-9.2',
    section: 9,
    sectionTitle: 'Parking',
    questionText: 'Are there any parking restrictions in the area (e.g. residents\' permits, yellow lines)?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 10: Other charges ──────────────────────────────────────
  {
    id: 'ta6-10.1',
    section: 10,
    sectionTitle: 'Other charges',
    questionText: 'Are there any charges or payments you make relating to the property (other than mortgage, council tax, and utilities)?',
    helpText: 'For example, estate maintenance charges, rent charges, or management fees.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-10.1a',
    section: 10,
    sectionTitle: 'Other charges',
    questionText: 'Please give details of the charges.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-10.1', answer: 'yes' },
  },
  {
    id: 'ta6-10.2',
    section: 10,
    sectionTitle: 'Other charges',
    questionText: 'Are you aware of any chancel repair liability affecting the property?',
    helpText: 'Chancel repair liability is an ancient obligation that can require property owners to contribute to the repair of a local Church of England church. It can be checked via a chancel repair liability search.',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 11: Occupiers ──────────────────────────────────────────
  {
    id: 'ta6-11.1',
    section: 11,
    sectionTitle: 'Occupiers',
    questionText: 'Does anyone other than you live at the property?',
    answerType: 'yesNo',
  },
  {
    id: 'ta6-11.1a',
    section: 11,
    sectionTitle: 'Occupiers',
    questionText: 'Please list the other occupiers and their relationship to you.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta6-11.1', answer: 'yes' },
  },

  // ── Section 12: Services ───────────────────────────────────────────
  {
    id: 'ta6-12.1',
    section: 12,
    sectionTitle: 'Services',
    questionText: 'What type of central heating does the property have?',
    answerType: 'multiChoice',
    choices: ['Gas', 'Electric', 'Oil', 'Solid fuel', 'Heat pump', 'None', 'Other'],
  },
  {
    id: 'ta6-12.2',
    section: 12,
    sectionTitle: 'Services',
    questionText: 'Has the boiler or heating system been serviced in the last 12 months?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-12.3',
    section: 12,
    sectionTitle: 'Services',
    questionText: 'Is the property connected to mains drainage?',
    answerType: 'yesNoNotKnown',
    fieldId: '7.1',
  },
  {
    id: 'ta6-12.4',
    section: 12,
    sectionTitle: 'Services',
    questionText: 'If not connected to mains drainage, what type of drainage system is used?',
    answerType: 'multiChoice',
    choices: ['Septic tank', 'Cesspit', 'Treatment plant', 'Other'],
    conditionalOn: { questionId: 'ta6-12.3', answer: 'no' },
  },

  // ── Section 13: Connection to utilities and services ───────────────
  {
    id: 'ta6-13.1',
    section: 13,
    sectionTitle: 'Connection to utilities and services',
    questionText: 'Is the property connected to mains water?',
    answerType: 'yesNoNotKnown',
    fieldId: '8.1',
  },
  {
    id: 'ta6-13.2',
    section: 13,
    sectionTitle: 'Connection to utilities and services',
    questionText: 'Is the property connected to mains electricity?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta6-13.3',
    section: 13,
    sectionTitle: 'Connection to utilities and services',
    questionText: 'Is the property connected to mains gas?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 14: Transaction information ────────────────────────────
  {
    id: 'ta6-14.1',
    section: 14,
    sectionTitle: 'Transaction information',
    questionText: 'Is the sale dependent on the seller buying another property?',
    answerType: 'yesNo',
  },
  {
    id: 'ta6-14.2',
    section: 14,
    sectionTitle: 'Transaction information',
    questionText: 'What date would you ideally like to complete the sale?',
    answerType: 'text',
    helpText: 'An approximate date is fine.',
  },
  {
    id: 'ta6-14.3',
    section: 14,
    sectionTitle: 'Transaction information',
    questionText: 'Is there anything else the buyer should know about the property?',
    helpText: 'Include anything not covered by the questions above that you think a buyer would want to know.',
    answerType: 'text',
  },
];

/**
 * Group questions into sections for display
 */
export function getTA6Sections(): TA6Section[] {
  const sectionMap = new Map<number, TA6Section>();

  for (const q of ta6Questions) {
    if (!sectionMap.has(q.section)) {
      sectionMap.set(q.section, {
        section: q.section,
        title: q.sectionTitle,
        questions: [],
      });
    }
    sectionMap.get(q.section)!.questions.push(q);
  }

  return Array.from(sectionMap.values()).sort((a, b) => a.section - b.section);
}

/**
 * Get all TA6 questions as a flat list
 */
export function getAllTA6Questions(): TA6Question[] {
  return ta6Questions;
}

/**
 * Count total answerable questions (excluding conditional ones whose parent isn't answered 'yes')
 */
export function countVisibleQuestions(answers: Record<string, string>): number {
  return ta6Questions.filter(q => {
    if (!q.conditionalOn) return true;
    return answers[q.conditionalOn.questionId] === q.conditionalOn.answer;
  }).length;
}

/**
 * Count answered questions
 */
export function countAnsweredQuestions(answers: Record<string, string>): number {
  return ta6Questions.filter(q => {
    if (!q.conditionalOn) return !!answers[q.id];
    if (answers[q.conditionalOn.questionId] !== q.conditionalOn.answer) return false;
    return !!answers[q.id];
  }).length;
}
